import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { People } from '../models/people';
import { CommonModule } from '@angular/common';
import { Role, RoleAdapter } from '../models/role';
import { Skill, SkillAdapter } from '../models/skill';
import { FileSelectEvent, FileUploadErrorEvent, FileUploadEvent, FileUploadModule } from 'primeng/fileupload';
import { HttpResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, combineLatest, finalize, map, Observable, of, tap } from 'rxjs';
import { RolesService } from '../services/roles.service';
import { SkillsService } from '../services/skills.service';
import { dateOrderValidator } from '../_utils/validators/dateValidator';
import { ToastService } from '../services/toast.service';
import { PagedResponse } from '../models/paged-response.model';
import { adaptPagedResponse } from '../operators/paged-response-operatros';



@Component({
  selector: 'app-people-add-modal',
  standalone: true,
  imports: [ReactiveFormsModule, FileUploadModule, CommonModule, TranslatePipe],
  templateUrl: './people-add-modal.component.html',
  styleUrl: './people-add-modal.component.scss'
})
export class PeopleAddModalComponent implements OnChanges, OnInit{



  @Input() personToEdit: People | null = null;
  @Output() personFormSubmitted = new EventEmitter<People | undefined>();
  
  roles$: Observable<Role[]>;
  skills$: Observable<Skill[]>;

  roles: Role[] = [];
  skills: Skill[] = [];

  personForm: FormGroup;

  // vars for avatar management
  apiUrl: string = environment.apiUrl;

  errorMessage: string = '';
  isLoadingData: boolean = true;
  formReady = false;

  constructor(
    private roleService: RolesService,
    private roleAdapter: RoleAdapter,
    private skillService: SkillsService,
    private skillAdapter: SkillAdapter,
    private toastService: ToastService,
    private fb: FormBuilder) {
    this.personForm = this.fb.group({
      id: [null],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      is_contractor: [false],
      hiring_date: ['',Validators.required,],
      exit_date: [''],
      role:[''],
      avatar:['']
      // We'll dynamically add the skill checkboxes thnks to setSkillsControls()
    }, {validators: dateOrderValidator('hiring_date', 'exit_date')});

    this.roles$ = this.fetchRoles().pipe(
      tap(roles => this.roles = roles)
    );

    this.skills$ = this.fetchSkills().pipe(
      tap(skills => this.skills = skills)
    );
  }

  ngOnInit(): void {
    // S'abonner aux données et initialiser le formulaire une fois que tout est chargé
    combineLatest([this.roles$, this.skills$]).pipe(
      finalize(() => {
        this.isLoadingData = false;
        this.setSkillsControls();
        this.initializeFormWithPersonData();
        this.formReady = true;
      })
    ).subscribe({
      error: (err) => {
        console.error('Error during data fetch (skills/roles):', err);
        this.errorMessage = 'Failed to load roles or skills.';
        this.isLoadingData = false;
      }
    });
  }

  private fetchRoles(): Observable<Role[]> {
    return this.roleService.getRoles().pipe(
      map((data: any[]) => data.map((item) => this.roleAdapter.adapt(item))),
      catchError((error: any) => {
        console.error('Error during roles fetch:', error.message);
        return of([]);
      })
    );
  }

  private fetchSkills(): Observable<Skill[]> {
    return this.skillService.getSkills(1, 35).pipe( 
        // 1. Applique l'opérateur personnalisé pour adapter tous les items
        adaptPagedResponse<Skill, Skill>(this.skillAdapter),
        // 2. Extrait uniquement le tableau 'items' qui contient les Skills adaptés
        map((response: PagedResponse<Skill>) => response.items), // Renvoie Skill[]

        catchError((error: any) => {
            console.error('Error during Skills fetch:', error.message);
            return of([]);
        })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['personToEdit'] && !changes['personToEdit'].firstChange){
      if(!this.isLoadingData && this.skills.length > 0 && this.roles.length > 0){
          console.log('ngOnChanges called. Calling initializeFormWithPersonData(). isLoadingData set to:', this.isLoadingData);
          this.initializeFormWithPersonData();
          this.formReady = true;
      }
    }
  }


  private initializeFormWithPersonData(): void {

    // this.personForm.reset();

    if (this.personToEdit) {
      this.personForm.patchValue({
        id: this.personToEdit.id,
        firstName: this.personToEdit.firstName,
        lastName: this.personToEdit.lastName,
        is_contractor: this.personToEdit.is_contractor,
        hiring_date: this.personToEdit.hiring_date,
        exit_date: this.personToEdit.exit_date,
        role: this.personToEdit.role ? this.personToEdit.role.id : '',
        avatar: this.personToEdit.avatar_url
      });

      this.skills.forEach(skill => {
        const isSelected = this.personToEdit?.skills?.some(s => s.id === skill.id) || false;
        if (this.personForm.contains('skill_' + skill.id)) {
          this.personForm.get('skill_' + skill.id)?.patchValue(isSelected);
        }
      });

    }else{
      //console.log('Initializing form for new person (reset to defaults).');
    }
  }

  private setSkillsControls(): void {

    this.skills.forEach(skill => {
      if (this.personForm.contains('skill_' + skill.id)) {
        this.personForm.removeControl('skill_' + skill.id);
      }
    });

    if (this.skills.length > 0) {
      this.skills.forEach(skill => {
        const isSelected = this.personToEdit?.skills.some(s => s.id === skill.id) || false;
        this.personForm.addControl('skill_' + skill.id, new FormControl(isSelected));
      });
    }
  }

  onSubmit(): void {
    if (this.personForm.valid) {
        const formValue = this.personForm.value;
        const selectedRole =  this.roles.find(role => role.id === parseInt(formValue.role)) || null; 
        // Get selected skills
        const selectedSkills = this.skills.filter(skill => formValue['skill_' + skill.id]);

        const newPerson = new People(
            this.personToEdit ? this.personToEdit.id : Date.now(), // fictif ID for new person
            formValue.firstName,
            formValue.lastName,
            formValue.is_contractor,
            formValue.hiring_date,
            formValue.exit_date,
            this.personToEdit?.teams || [],
            this.personToEdit?.createdAt || new Date(),
            new Date(),
            selectedSkills,
            selectedRole,
            formValue.avatar
        );
        this.personFormSubmitted.emit(newPerson);
    }
}

  onClose(): void {
    this.personFormSubmitted.emit(undefined);
  }

  clearExitDate(): void {
    this.personForm.get('exit_date')?.setValue(null);
  }
  
/**
 * Handles the avatar file selection event.
 * 
 * When an avatar file is selected, this method creates a temporary URL for the
 * file and updates the form control with the new avatar URL. So that the file is displayed
 * in the form before its efffective upload.
 * 
 * @param $event - The file selection event containing the selected file(s).
 */
  onAvatarSelect($event:  FileSelectEvent) {
    console.log('Avatar file selected:', $event);
    const file = $event.files[0];
    const fileUrl = URL.createObjectURL(file);
    this.personForm.patchValue({
      avatar: fileUrl
    });
  }

  onAvatarUpload($event: FileUploadEvent) {
    console.log('Avatar file uploaded:', $event);
    if(this.personToEdit){
      const httpResponse = $event.originalEvent as HttpResponse<any>;
      this.personToEdit.avatar = httpResponse.body.avatar_url;
      this.toastService.showSuccess('COMMON.UPDATE_SUCCESS');
    }
  }

  onAvatarUploadError($event: FileUploadErrorEvent) {
    console.log('Avatar file uploaded ERROR:', $event);
    this.toastService.showError('COMMON.UPDATE_ERROR');
  }

  roleTrackBy(index: number, role: Role): number {
    return role.id;
  }

  skillTrackBy(index: number, skill: Skill): number {
    return skill.id;
  }
}
